// ============================================
// Generic-to-Formulary Mapping Service
//
// Bridges Dr. C's generic supplement names
// (e.g. "Ashwagandha 600mg") to HemoLogix
// formulary product IDs (e.g. "supp-ashwagandha-ksm66").
//
// Collection:
//   platform/calibration/generic_formulary_map/{mappingId}
// ============================================

import {
  collection, doc, getDoc, getDocs, setDoc, deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { GenericFormularyMapping } from '../../types/labUpload';

import { C_PLATFORM, DOC_CALIBRATION, SC_GENERIC_FORMULARY_MAP } from '../../constants/collections';
// ── Collection ref ──

const mappingsCol = () =>
  collection(db, C_PLATFORM, DOC_CALIBRATION, SC_GENERIC_FORMULARY_MAP);

// ── CRUD ──

/** Get all generic-to-formulary mappings. */
export async function getAllMappings(): Promise<GenericFormularyMapping[]> {
  const snap = await getDocs(mappingsCol());
  return snap.docs.map((d) => d.data() as GenericFormularyMapping);
}

/** Get a single mapping by ID. */
export async function getMapping(
  mappingId: string,
): Promise<GenericFormularyMapping | null> {
  const snap = await getDoc(doc(mappingsCol(), mappingId));
  return snap.exists() ? (snap.data() as GenericFormularyMapping) : null;
}

/** Save or update a mapping. */
export async function saveMapping(
  mapping: GenericFormularyMapping,
): Promise<string> {
  const id = mapping.mappingId;

  await setDoc(doc(mappingsCol(), id), {
    ...stripUndefined(mapping),
    _updatedAt: Timestamp.now(),
  });

  return id;
}

/** Delete a mapping by ID. */
export async function deleteMapping(mappingId: string): Promise<void> {
  await deleteDoc(doc(mappingsCol(), mappingId));
}

/**
 * Look up a formulary product ID for a generic supplement name.
 * Case-insensitive partial match.
 */
export async function resolveGenericToFormulary(
  genericName: string,
): Promise<GenericFormularyMapping | null> {
  const all = await getAllMappings();
  const normalized = genericName.toLowerCase().trim();

  // Exact match first
  const exact = all.find(
    (m) => m.genericName.toLowerCase().trim() === normalized,
  );
  if (exact) return exact;

  // Partial match: generic name is contained in or contains the mapping name
  const partial = all.find(
    (m) =>
      normalized.includes(m.genericName.toLowerCase().trim()) ||
      m.genericName.toLowerCase().trim().includes(normalized),
  );

  return partial ?? null;
}

// ── Utils ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
